import React from 'react';

interface StatusTrackerProps {
  status: string;
  showLegend?: boolean;
}

const stages = [
  { key: 'I', label: 'Invited', status: 'Invited', color: 'indigo' },
  { key: 'S', label: 'Scheduled', status: 'Scheduled', color: 'violet' },
  { key: 'C', label: 'Confirmed', status: 'Confirmed', color: 'blue' },
  { key: 'L', label: 'Live', status: 'Live', color: 'emerald' },
  { key: 'F', label: 'Completed', status: 'Completed', color: 'green' },
];

const getStageColor = (currentStatus: string, stageStatus: string) => {
  // If current status is Completed, show all stages as completed
  if (currentStatus === 'Completed') {
    return 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg shadow-green-200';
  }

  const stageOrder = stages.findIndex((s) => s.status === stageStatus);
  const currentStageOrder = stages.findIndex((s) => s.status === currentStatus);

  // Show completed stages up to current stage
  if (stageOrder <= currentStageOrder) {
    switch (stageStatus) {
      case 'Invited':
        return 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-lg shadow-indigo-200';
      case 'Scheduled':
        return 'bg-gradient-to-r from-violet-500 to-violet-600 text-white shadow-lg shadow-violet-200';
      case 'Confirmed':
        return 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-200';
      case 'Live':
        return 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-200';
      case 'Completed':
        return 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg shadow-green-200';
      default:
        return 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-500';
    }
  }
  return 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-500';
};

const getConnectorColor = (currentStatus: string, stageStatus: string) => {
  if (currentStatus === 'Completed') {
    return 'bg-gradient-to-r from-green-400 to-green-500';
  }

  const stageOrder = stages.findIndex((s) => s.status === stageStatus);
  const currentStageOrder = stages.findIndex((s) => s.status === currentStatus);

  // Show colored connector up to current stage
  if (stageOrder < currentStageOrder) {
    switch (currentStatus) {
      case 'Scheduled':
        return 'bg-gradient-to-r from-violet-400 to-violet-500';
      case 'Confirmed':
        return 'bg-gradient-to-r from-blue-400 to-blue-500';
      case 'Live':
        return 'bg-gradient-to-r from-emerald-400 to-emerald-500';
      case 'Completed':
        return 'bg-gradient-to-r from-green-400 to-green-500';
      default:
        return 'bg-gradient-to-r from-indigo-400 to-indigo-500';
    }
  }
  return 'bg-gradient-to-r from-gray-200 to-gray-300';
};

const StatusTracker: React.FC<StatusTrackerProps> = ({
  status = 'unknown',
}) => {
  return (
    <div className='space-y-3'>
      <div className='flex items-center gap-1'>
        { stages.map((stage, index) => (
          <div key={stage.key} className='flex items-center'>
            <div
                className={`
                w-6 h-6 flex items-center justify-center rounded
                ${getStageColor(status, stage.status)}
                font-medium text-sm transition-all duration-200
                ${
                  status === stage.status &&
                  !['unknown', 'Completed'].includes(status) &&
                  'ring-1 ring-opacity-50 ring-offset-1 ring-' +
                    stage.color +
                    '-400'
                }
              `}
            >
              { stage.key }
            </div>
            { index < stages.length - 1 && (
              <div
                  className={`h-0.5 w-3 mx-1 ${getConnectorColor(
                    status,
                    stage.status,
                  )}`}
              />
            ) }
          </div>
        )) }
      </div>
    </div>
  );
};

export default StatusTracker;
